import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthContext } from "../../contexts/AuthContext";
import { getTopStorageUsers } from "../../services/adminService";
import { HardDrive, ArrowLeft, Home, Search, TrendingUp, AlertTriangle } from "lucide-react";
import Card from "../../components/Card";

interface StorageUser {
  uid: string;
  email: string;
  name: string;
  usedBytes: number;
  limitBytes: number;
  usagePercent: number;
}

export default function StorageManagement() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [storageUsers, setStorageUsers] = useState<StorageUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (user?.email !== "viitamint@gmail.com") {
      navigate("/");
      return;
    }

    loadStorageData();
  }, [user, navigate]);

  const loadStorageData = async () => {
    try {
      // Load all users with storage data (not limited to top 5)
      const users = await getTopStorageUsers(1000); // Get all users
      setStorageUsers(users);
    } catch (error) {
      console.error("Error loading storage data:", error);
    } finally {
      setLoading(false);
    }
  };

  const bytesToGB = (bytes: number) => bytes / (1024 * 1024 * 1024);

  const filteredUsers = storageUsers.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalUsedGB = storageUsers.reduce((sum, user) => sum + bytesToGB(user.usedBytes), 0);
  const totalLimitGB = storageUsers.reduce((sum, user) => sum + bytesToGB(user.limitBytes), 0);
  const averageUsagePercent = storageUsers.length > 0 
    ? storageUsers.reduce((sum, user) => sum + user.usagePercent, 0) / storageUsers.length 
    : 0;
  const warningCount = storageUsers.filter(user => user.usagePercent > 80).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div>
                <h1 className="text-4xl font-bold text-textDark">จัดการพื้นที่จัดเก็บ</h1>
                <p className="text-textLight">ติดตามการใช้พื้นที่จัดเก็บของผู้สอนทั้งหมด</p>
              </div>
            </div>
            <Link to="/">
              <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                <Home className="w-5 h-5" />
                <span>ไปหน้าแรก</span>
              </button>
            </Link>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-4 gap-6 mb-6">
            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <HardDrive className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">พื้นที่ใช้ไปทั้งหมด</p>
                  <p className="text-2xl font-bold text-textDark">{totalUsedGB.toFixed(2)} GB</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">พื้นที่ทั้งหมด</p>
                  <p className="text-2xl font-bold text-textDark">{totalLimitGB.toFixed(0)} GB</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                  <HardDrive className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">การใช้งานเฉลี่ย</p>
                  <p className="text-2xl font-bold text-textDark">{averageUsagePercent.toFixed(1)}%</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">ใกล้เต็ม (&gt;80%)</p>
                  <p className="text-2xl font-bold text-yellow-600">{warningCount}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textLight" />
            <input
              type="text"
              placeholder="ค้นหาผู้สอน (ชื่อหรืออีเมล)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        {/* Storage Users Table */}
        <Card>
          <div className="p-6">
            <h2 className="text-2xl font-bold text-textDark mb-6">ผู้สอนทั้งหมด ({filteredUsers.length})</h2>

            {filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <HardDrive className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-textLight">ไม่พบข้อมูล</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredUsers.map((user, index) => {
                  const isWarning = user.usagePercent > 80;
                  const isDanger = user.usagePercent > 100;
                  
                  return (
                    <div 
                      key={user.uid} 
                      className={`border-2 rounded-xl p-4 transition-all hover:shadow-lg ${
                        isDanger ? 'border-red-200 bg-red-50' : 
                        isWarning ? 'border-yellow-200 bg-yellow-50' : 
                        'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                            isDanger ? 'bg-red-500' : 
                            isWarning ? 'bg-yellow-500' : 
                            'bg-blue-500'
                          }`}>
                            #{index + 1}
                          </div>
                          <div>
                            <p className="font-bold text-textDark text-lg">{user.name || 'ไม่ระบุชื่อ'}</p>
                            <p className="text-sm text-textLight">{user.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-3xl font-bold ${
                            isDanger ? 'text-red-600' : 
                            isWarning ? 'text-yellow-600' : 
                            'text-green-600'
                          }`}>
                            {user.usagePercent.toFixed(1)}%
                          </p>
                          <p className="text-sm text-textLight">
                            {bytesToGB(user.usedBytes).toFixed(2)} / {bytesToGB(user.limitBytes).toFixed(0)} GB
                          </p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div
                          className={`h-4 rounded-full transition-all ${
                            isDanger ? 'bg-red-500' : 
                            isWarning ? 'bg-yellow-500' : 
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(user.usagePercent, 100)}%` }}
                        ></div>
                      </div>

                      {/* Warning Message */}
                      {isDanger && (
                        <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-semibold">เกินโควต้าแล้ว! ควรเพิ่มพื้นที่</span>
                        </div>
                      )}
                      {isWarning && !isDanger && (
                        <div className="mt-3 flex items-center gap-2 text-yellow-600 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-semibold">ใกล้เต็มแล้ว กรุณาติดตาม</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
